import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { FaUser, FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaSave, FaTimes, FaUserShield, FaUsers, FaSearch, FaIdCard, FaClock } from 'react-icons/fa';
import apiService from '../services/api';

const Container = styled.div`
  background: #fafbfc;
  min-height: 100vh;
  color: #1a202c;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', system-ui, sans-serif;
  margin: 0 auto;
  padding: clamp(1rem, 2vw, 1.5rem);
  max-width: 1440px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: clamp(0.875rem, 1.5vw, 1.25rem);
  }

  @media (max-width: 768px) {
    padding: clamp(0.75rem, 1.25vw, 1rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5rem, 1vw, 0.75rem);
  }
`;

const Header = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  padding: clamp(1rem, 2vw, 1.5rem);
  margin-bottom: clamp(1rem, 2vw, 1.5rem);
  gap: clamp(0.75rem, 1.25vw, 1rem);
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: clamp(0.875rem, 1.5vw, 1.25rem);
    gap: clamp(0.625rem, 1vw, 0.875rem);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    padding: clamp(0.75rem, 1.25vw, 1rem);
    gap: clamp(0.5rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.625rem, 1vw, 0.875rem);
    gap: clamp(0.375rem, 0.75vw, 0.625rem);
  }
`;

const Title = styled.h1`
  color: #1a202c;
  margin: 0;
  font-weight: 700;
  display: flex;
  align-items: center;
  letter-spacing: -0.5px;
  line-height: 1.2;
  font-size: clamp(1.125rem, 1.75vw, 1.375rem);
  gap: clamp(0.375rem, 0.625vw, 0.5rem);

  @media (max-width: 1200px) {
    font-size: clamp(1rem, 1.5vw, 1.25rem);
  }

  @media (max-width: 768px) {
    font-size: clamp(0.9375rem, 1.25vw, 1.125rem);
    justify-content: center;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.875rem, 1.125vw, 1rem);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: clamp(0.625rem, 1vw, 0.75rem);
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 1200px) {
    gap: clamp(0.5rem, 0.875vw, 0.625rem);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: clamp(0.375rem, 0.625vw, 0.5rem);
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: clamp(0.25rem, 0.5vw, 0.375rem);
  }
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled.input`
  border: 1px solid #e2e8f0;
  background: white;
  color: #2d3748;
  transition: all 0.2s ease;
  padding: clamp(0.5rem, 0.875vw, 0.625rem) clamp(0.75rem, 1.125vw, 0.875rem) clamp(0.5rem, 0.875vw, 0.625rem) clamp(2rem, 2.5vw, 2.25rem);
  font-size: clamp(0.75rem, 1vw, 0.8125rem);
  width: clamp(10rem, 15vw, 12rem);
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }

  @media (max-width: 1200px) {
    width: clamp(8rem, 12vw, 10rem);
    padding: clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(0.625rem, 1vw, 0.75rem) clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(1.75rem, 2.25vw, 2rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.625rem, 0.875vw, 0.75rem) clamp(2.25rem, 2.75vw, 2.5rem);
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(2rem, 2.5vw, 2.25rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: clamp(0.5rem, 0.75vw, 0.625rem);
  color: #666;
  font-size: clamp(0.75rem, 1vw, 0.8125rem);

  @media (max-width: 1200px) {
    left: clamp(0.4375rem, 0.625vw, 0.5625rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 768px) {
    left: clamp(0.625rem, 0.875vw, 0.75rem);
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
  }

  @media (max-width: 480px) {
    left: clamp(0.5625rem, 0.75vw, 0.6875rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }
`;

const FilterSelect = styled.select`
  padding: clamp(0.5rem, 0.875vw, 0.625rem) clamp(0.75rem, 1.125vw, 0.875rem);
  border: 1px solid #e2e8f0;
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  background: white;
  color: #2d3748;
  font-size: clamp(0.75rem, 1vw, 0.8125rem);
  min-width: clamp(6rem, 10vw, 8rem);
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }

  @media (max-width: 1200px) {
    min-width: clamp(5rem, 8vw, 6.5rem);
    padding: clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(0.625rem, 1vw, 0.75rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    padding: clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.625rem, 0.875vw, 0.75rem);
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.5625rem, 0.75vw, 0.6875rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }
`;

const AddButton = styled.button`
  padding: clamp(0.5rem, 0.875vw, 0.625rem) clamp(0.875rem, 1.25vw, 1rem);
  background: linear-gradient(135deg, #4299e1, #3182ce);
  color: white;
  border: none;
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.75rem, 1vw, 0.8125rem);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: clamp(0.25rem, 0.375vw, 0.375rem);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
  box-sizing: border-box;
  
  &:hover {
    background: linear-gradient(135deg, #3182ce, #2c5282);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(66, 153, 225, 0.4);
  }

  @media (max-width: 1200px) {
    padding: clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(0.75rem, 1.125vw, 0.875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    gap: clamp(0.1875rem, 0.3125vw, 0.3125rem);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.875rem, 1.25vw, 1rem);
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.75rem, 1vw, 0.875rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: clamp(0.75rem, 1.25vw, 1rem);
  margin-bottom: clamp(1rem, 2vw, 1.5rem);
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: clamp(0.625rem, 1vw, 0.875rem);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: clamp(0.5rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0.375rem, 0.75vw, 0.625rem);
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  padding: clamp(1rem, 1.5vw, 1.25rem);
  text-align: center;
  color: #1a202c;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  min-height: clamp(4rem, 6vw, 5rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  }
  
  h3 {
    margin: 0 0 clamp(0.25rem, 0.375vw, 0.375rem) 0;
    font-size: clamp(1.25rem, 2vw, 1.5rem);
    font-weight: 700;
    color: #4299e1;
  }
  
  p {
    margin: 0;
    color: #64748b;
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    font-weight: 500;
  }

  @media (max-width: 1200px) {
    padding: clamp(0.875rem, 1.25vw, 1rem);
    min-height: clamp(3.5rem, 5vw, 4rem);
    
    h3 {
      font-size: clamp(1.125rem, 1.75vw, 1.25rem);
    }
    
    p {
      font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    }
  }

  @media (max-width: 768px) {
    padding: clamp(0.75rem, 1vw, 0.875rem);
    min-height: clamp(3rem, 4vw, 3.5rem);
    
    h3 {
      font-size: clamp(1rem, 1.5vw, 1.125rem);
    }
    
    p {
      font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    }
  }

  @media (max-width: 480px) {
    padding: clamp(0.625rem, 0.875vw, 0.75rem);
    min-height: clamp(2.5rem, 3.5vw, 3rem);
    
    h3 {
      font-size: clamp(0.9375rem, 1.25vw, 1rem);
    }
    
    p {
      font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    }
  }
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: clamp(1rem, 1.5vw, 1.25rem);
  margin-bottom: clamp(1rem, 2vw, 1.5rem);

  @media (max-width: 1366px) {
    grid-template-columns: repeat(3, 1fr);
    gap: clamp(0.875rem, 1.25vw, 1rem);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: clamp(0.75rem, 1vw, 0.875rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0.625rem, 0.875vw, 0.75rem);
  }
`;

const UserCard = styled.div`
  background: white;
  border: 1px solid #f1f5f9;
  color: #1a202c;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    border-color: #e2e8f0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: ${props => props.isAdmin ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'linear-gradient(135deg, #48bb78, #38a169)'};
    height: clamp(0.125rem, 0.25vw, 0.25rem);
  }

  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  padding: clamp(0.875rem, 1.25vw, 1rem);

  @media (max-width: 1200px) {
    padding: clamp(0.75rem, 1vw, 0.875rem);
    border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  }

  @media (max-width: 768px) {
    padding: clamp(0.625rem, 0.875vw, 0.75rem);
    border-radius: clamp(0.25rem, 0.5vw, 0.5rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5rem, 0.75vw, 0.625rem);
    border-radius: clamp(0.1875rem, 0.375vw, 0.375rem);
  }
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: clamp(0.625rem, 1vw, 0.75rem);
  margin-bottom: clamp(1rem, 1.5vw, 1.25rem);

  @media (max-width: 1200px) {
    gap: clamp(0.5rem, 0.875vw, 0.625rem);
    margin-bottom: clamp(0.875rem, 1.25vw, 1rem);
  }

  @media (max-width: 768px) {
    gap: clamp(0.375rem, 0.625vw, 0.5rem);
    margin-bottom: clamp(0.75rem, 1vw, 0.875rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0.25rem, 0.5vw, 0.375rem);
    margin-bottom: clamp(0.625rem, 0.875vw, 0.75rem);
  }
`;

const UserAvatar = styled.div`
  background: ${props => props.isAdmin
    ? 'linear-gradient(135deg, #667eea, #764ba2)'
    : 'linear-gradient(135deg, #48bb78, #38a169)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 6px 20px ${props => props.isAdmin
    ? 'rgba(102, 126, 234, 0.4)'
    : 'rgba(72, 187, 120, 0.4)'};
  flex-shrink: 0;
  width: clamp(3rem, 4vw, 3.5rem);
  height: clamp(3rem, 4vw, 3.5rem);
  font-size: clamp(1rem, 1.5vw, 1.25rem);

  @media (max-width: 1200px) {
    width: clamp(2.5rem, 3.5vw, 3rem);
    height: clamp(2.5rem, 3.5vw, 3rem);
    font-size: clamp(0.875rem, 1.25vw, 1rem);
  }

  @media (max-width: 768px) {
    width: clamp(2.25rem, 3vw, 2.5rem);
    height: clamp(2.25rem, 3vw, 2.5rem);
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
  }

  @media (max-width: 480px) {
    width: clamp(2rem, 2.5vw, 2.25rem);
    height: clamp(2rem, 2.5vw, 2.25rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h3`
  margin: 0;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  font-size: clamp(0.875rem, 1.25vw, 1rem);
  margin-bottom: clamp(0.25rem, 0.375vw, 0.375rem);

  @media (max-width: 1200px) {
    font-size: clamp(0.8125rem, 1.125vw, 0.9375rem);
    margin-bottom: clamp(0.1875rem, 0.3125vw, 0.3125rem);
  }

  @media (max-width: 768px) {
    font-size: clamp(0.75rem, 1vw, 0.875rem);
    margin-bottom: clamp(0.125rem, 0.25vw, 0.25rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.6875rem, 0.875vw, 0.8125rem);
    margin-bottom: clamp(0.0625rem, 0.1875vw, 0.1875rem);
  }
`;

const UserRole = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${props => props.role === 'admin'
    ? 'linear-gradient(135deg, #667eea, #764ba2)'
    : 'linear-gradient(135deg, #48bb78, #38a169)'};
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 8px ${props => props.role === 'admin'
    ? 'rgba(102, 126, 234, 0.3)'
    : 'rgba(72, 187, 120, 0.3)'};
  white-space: nowrap;
  flex-shrink: 0;
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
  padding: clamp(0.1875rem, 0.375vw, 0.375rem) clamp(0.5rem, 0.75vw, 0.625rem);
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.5625rem, 0.75vw, 0.625rem);

  @media (max-width: 1200px) {
    gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
    padding: clamp(0.125rem, 0.25vw, 0.25rem) clamp(0.375rem, 0.625vw, 0.5rem);
    border-radius: clamp(0.3125rem, 0.5vw, 0.4375rem);
    font-size: clamp(0.5rem, 0.625vw, 0.5625rem);
  }

  @media (max-width: 768px) {
    gap: clamp(0.0625rem, 0.125vw, 0.125rem);
    padding: clamp(0.0625rem, 0.1875vw, 0.1875rem) clamp(0.25rem, 0.5vw, 0.375rem);
    border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
    font-size: clamp(0.4375rem, 0.5625vw, 0.5rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0rem, 0.0625vw, 0.0625rem);
    padding: clamp(0rem, 0.125vw, 0.125rem) clamp(0.1875rem, 0.375vw, 0.3125rem);
    border-radius: clamp(0.1875rem, 0.25vw, 0.3125rem);
    font-size: clamp(0.375rem, 0.5vw, 0.4375rem);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.375rem, 0.625vw, 0.5rem);
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: clamp(0.5rem, 0.75vw, 0.625rem);
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.375rem, 0.625vw, 0.5rem);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  color: #2d3748;
  gap: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  padding: clamp(0.1875rem, 0.25vw, 0.25rem) 0;

  @media (max-width: 1200px) {
    gap: clamp(0.3125rem, 0.5vw, 0.4375rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    padding: clamp(0.125rem, 0.1875vw, 0.1875rem) 0;
  }

  @media (max-width: 768px) {
    gap: clamp(0.25rem, 0.375vw, 0.375rem);
    font-size: clamp(0.5625rem, 0.75vw, 0.625rem);
    padding: clamp(0.0625rem, 0.125vw, 0.125rem) 0;
  }

  @media (max-width: 480px) {
    gap: clamp(0.1875rem, 0.25vw, 0.3125rem);
    font-size: clamp(0.5rem, 0.625vw, 0.5625rem);
    padding: clamp(0rem, 0.0625vw, 0.0625rem) 0;
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: clamp(0.75rem, 1.25vw, 1rem) 0;
  padding: clamp(0.625rem, 1vw, 0.75rem);
  background: #f8fafc;
  border-radius: clamp(0.5rem, 1vw, 0.75rem);
  border: 1px solid #e2e8f0;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    margin: clamp(0.625rem, 1vw, 0.875rem) 0;
    padding: clamp(0.5rem, 0.875vw, 0.625rem);
    border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
  }

  @media (max-width: 768px) {
    margin: clamp(0.5rem, 0.875vw, 0.75rem) 0;
    padding: clamp(0.375rem, 0.75vw, 0.5rem);
    border-radius: clamp(0.25rem, 0.5vw, 0.5rem);
    flex-direction: column;
    gap: clamp(0.375rem, 0.625vw, 0.5rem);
    align-items: stretch;
  }

  @media (max-width: 480px) {
    margin: clamp(0.375rem, 0.75vw, 0.625rem) 0;
    padding: clamp(0.25rem, 0.625vw, 0.375rem);
    border-radius: clamp(0.1875rem, 0.375vw, 0.375rem);
    gap: clamp(0.25rem, 0.5vw, 0.375rem);
  }
`;

const UserActions = styled.div`
  display: flex;
  gap: clamp(1rem, 1.5vw, 1.25rem);
  margin-top: auto;
  padding-top: clamp(0.875rem, 1.25vw, 1rem);
  
  @media (max-width: 1200px) {
    gap: clamp(0.875rem, 1.25vw, 1rem);
    padding-top: clamp(0.75rem, 1vw, 0.875rem);
  }
  
  @media (max-width: 768px) {
    gap: clamp(0.75rem, 1vw, 0.875rem);
    padding-top: clamp(0.625rem, 0.875vw, 0.75rem);
    flex-direction: column;
  }

  @media (max-width: 480px) {
    gap: clamp(0.625rem, 0.875vw, 0.75rem);
    padding-top: clamp(0.5rem, 0.75vw, 0.625rem);
  }
`;

const ActionButton = styled.button`
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 600;
  padding: clamp(0.5rem, 0.875vw, 0.625rem) clamp(0.75rem, 1.125vw, 0.875rem);
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
  box-sizing: border-box;
  
  ${props => {
    switch (props.type) {
      case 'edit':
        return `
          background: linear-gradient(135deg, #4299e1, #3182ce);
          color: white;
          box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
          &:hover { 
            background: linear-gradient(135deg, #3182ce, #2c5282);
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(66, 153, 225, 0.4);
          }
        `;
      case 'delete':
        return `
          background: linear-gradient(135deg, #f56565, #e53e3e);
          color: white;
          box-shadow: 0 4px 12px rgba(245, 101, 101, 0.3);
          &:hover { 
            background: linear-gradient(135deg, #e53e3e, #c53030);
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(245, 101, 101, 0.4);
          }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #4a5568;
          border: 1px solid #e2e8f0;
          &:hover { 
            background: #e2e8f0;
            transform: translateY(-1px);
          }
        `;
    }
  }}

  @media (max-width: 1200px) {
    padding: clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(0.625rem, 1vw, 0.75rem);
    font-size: clamp(0.5625rem, 0.625vw, 0.625rem);
    gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: clamp(0.5rem, 0.875vw, 0.625rem) clamp(0.75rem, 1.125vw, 0.875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.4375rem, 0.75vw, 0.5625rem) clamp(0.625rem, 1vw, 0.75rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${props => props.status === 'active'
    ? 'linear-gradient(135deg, #48bb78, #38a169)'
    : 'linear-gradient(135deg, #f56565, #e53e3e)'};
  color: white;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 8px ${props => props.status === 'active'
    ? 'rgba(72, 187, 120, 0.3)'
    : 'rgba(245, 101, 101, 0.3)'};
  white-space: nowrap;
  flex-shrink: 0;
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
  padding: clamp(0.4375rem, 0.625vw, 0.5rem) clamp(0.625rem, 0.875vw, 0.75rem);
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.5625rem, 0.75vw, 0.625rem);

  @media (max-width: 1200px) {
    gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
    padding: clamp(0.375rem, 0.5vw, 0.4375rem) clamp(0.5rem, 0.75vw, 0.625rem);
    border-radius: clamp(0.3125rem, 0.5vw, 0.4375rem);
    font-size: clamp(0.5rem, 0.625vw, 0.5625rem);
  }

  @media (max-width: 768px) {
    gap: clamp(0.0625rem, 0.125vw, 0.125rem);
    padding: clamp(0.3125rem, 0.5vw, 0.375rem) clamp(0.375rem, 0.625vw, 0.5rem);
    border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
    font-size: clamp(0.4375rem, 0.5625vw, 0.5rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0rem, 0.0625vw, 0.0625rem);
    padding: clamp(0.25rem, 0.375vw, 0.3125rem) clamp(0.25rem, 0.5vw, 0.375rem);
    border-radius: clamp(0.1875rem, 0.25vw, 0.3125rem);
    font-size: clamp(0.375rem, 0.5vw, 0.4375rem);
  }
`;

const UserEmployeeId = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 1px solid #bae6fd;
  color: #1e40af;
  font-weight: 600;
  width: auto;
  white-space: nowrap;
  flex-shrink: 0;
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
  padding: clamp(0.4375rem, 0.625vw, 0.5rem) clamp(0.625rem, 0.875vw, 0.75rem);
  border-radius: clamp(0.375rem, 0.625vw, 0.5rem);
  font-size: clamp(0.5625rem, 0.75vw, 0.625rem);

  @media (max-width: 1200px) {
    gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
    padding: clamp(0.375rem, 0.5vw, 0.4375rem) clamp(0.5rem, 0.75vw, 0.625rem);
    border-radius: clamp(0.3125rem, 0.5vw, 0.4375rem);
    font-size: clamp(0.5rem, 0.625vw, 0.5625rem);
  }

  @media (max-width: 768px) {
    gap: clamp(0.0625rem, 0.125vw, 0.125rem);
    padding: clamp(0.3125rem, 0.5vw, 0.375rem) clamp(0.375rem, 0.625vw, 0.5rem);
    border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
    font-size: clamp(0.4375rem, 0.5625vw, 0.5rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0rem, 0.0625vw, 0.0625rem);
    padding: clamp(0.25rem, 0.375vw, 0.3125rem) clamp(0.25rem, 0.5vw, 0.375rem);
    border-radius: clamp(0.1875rem, 0.25vw, 0.3125rem);
    font-size: clamp(0.375rem, 0.5vw, 0.4375rem);
  }
`;

const ActivityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.1875rem, 0.25vw, 0.25rem);
  font-size: clamp(0.5625rem, 0.75vw, 0.625rem);
  color: #64748b;
`;

// Modal Components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  padding: clamp(0.75rem, 1.5vw, 1rem);
  
  @media (max-width: 768px) {
    padding: clamp(0.5rem, 1vw, 0.75rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.25rem, 0.75vw, 0.5rem);
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0;
  padding: clamp(1.5rem, 2.5vw, 1.75rem);
  width: 100%;
  max-width: 600px;
  color: #1a202c;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 5px solid #e8f2fd;
  position: relative;
  box-sizing: border-box;
  border-radius: 8px;
  
  @media (max-width: 1200px) {
    max-width: 700px;
    padding: clamp(1.25rem, 2vw, 1.5rem);
  }
  
  @media (max-width: 768px) {
    padding: clamp(1rem, 1.5vw, 1.25rem);
    border-radius: clamp(0.5rem, 1vw, 0.75rem);
    max-width: 95%;
    max-height: 95vh;
  }

  @media (max-width: 480px) {
    padding: clamp(0.75rem, 1.25vw, 1rem);
    border-radius: clamp(0.375rem, 0.75vw, 0.625rem);
    max-width: 98%;
    max-height: 98vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(1rem, 1.5vw, 1.25rem);
  padding-bottom: clamp(0.75rem, 1vw, 0.875rem);
  border-bottom: 1px solid #f1f5f9);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  font-weight: 700;
  color: #1a202c;

  @media (max-width: 768px) {
    font-size: clamp(0.9375rem, 1.25vw, 1.125rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.875rem, 1.125vw, 1rem);
  }
`;

const CloseButton = styled.button`
  background: #f1f5f9;
  border: none;
  color: #64748b;
  width: clamp(2rem, 3vw, 2.5rem);
  height: clamp(2rem, 3vw, 2.5rem);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e2e8f0;
    color: #2d3748;
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    width: clamp(1.75rem, 2.5vw, 2.25rem);
    height: clamp(1.75rem, 2.5vw, 2.25rem);
  }

  @media (max-width: 480px) {
    width: clamp(1.5rem, 2vw, 2rem);
    height: clamp(1.5rem, 2vw, 2rem);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: clamp(0.875rem, 1.25vw, 1rem);
  
  @media (max-width: 768px) {
    gap: clamp(0.75rem, 1vw, 0.875rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0.625rem, 0.875vw, 0.75rem);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.25rem, 0.375vw, 0.375rem);
`;

const Label = styled.label`
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  font-weight: 700;
  color: #000000;

  @media (max-width: 768px) {
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
  }
`;

const Input = styled.input`
  padding: clamp(0.625rem, 1vw, 0.75rem) clamp(0.875rem, 1.125vw, 1rem);
  border: 1px solid #e2e8f0;
  border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  background: white;
  color: #2d3748;
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  &::placeholder {
    color: #a0aec0;
  }

  @media (max-width: 768px) {
    padding: clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.75rem, 1vw, 0.875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.625rem, 0.875vw, 0.75rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    border-radius: clamp(0.4375rem, 0.75vw, 0.5625rem);
  }
`;

const Select = styled.select`
  padding: clamp(0.625rem, 1vw, 0.75rem) clamp(0.875rem, 1.125vw, 1rem);
  border: 1px solid #e2e8f0;
  border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  background: white;
  color: #2d3748;
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
  
  &:hover {
    border-color: #cbd5e0;
  }
  
  option {
    background: white;
    color: #2d3748;
  }

  @media (max-width: 768px) {
    padding: clamp(0.625rem, 0.875vw, 0.75rem) clamp(0.75rem, 1vw, 0.875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.75vw, 0.6875rem) clamp(0.625rem, 0.875vw, 0.75rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    border-radius: clamp(0.4375rem, 0.75vw, 0.5625rem);
  }
`;

const PasswordGroup = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: clamp(0.625rem, 0.875vw, 0.75rem);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 0;
  font-size: clamp(0.75rem, 1vw, 0.875rem);
  
  &:hover {
    color: #4a5568;
  }

  @media (max-width: 768px) {
    right: clamp(0.625rem, 0.875vw, 0.75rem);
    font-size: clamp(0.75rem, 1vw, 0.875rem);
  }

  @media (max-width: 480px) {
    right: clamp(0.5625rem, 0.75vw, 0.6875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.8125rem);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: clamp(0.75rem, 1.125vw, 0.875rem);
  justify-content: flex-end;
  margin-top: clamp(1rem, 1.5vw, 1.25rem);
  padding-top: clamp(0.75rem, 1vw, 0.875rem);
  border-top: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: clamp(0.5rem, 0.75vw, 0.625rem);
  }

  @media (max-width: 480px) {
    gap: clamp(0.375rem, 0.625vw, 0.5rem);
  }
`;

const Button = styled.button`
  padding: clamp(0.625rem, 1vw, 0.75rem) clamp(1rem, 1.5vw, 1.25rem);
  border: none;
  border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  cursor: pointer;
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: clamp(0.25rem, 0.375vw, 0.375rem);
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #4299e1, #3182ce);
    color: white;
    box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
    &:hover {
      background: linear-gradient(135deg, #3182ce, #2c5282);
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(66, 153, 225, 0.4);
    }
  ` : `
    background: #f1f5f9;
    color: #64748b;
    border: 1px solid #e2e8f0;
    &:hover {
      background: #e2e8f0;
      color: #2d3748;
      transform: translateY(-1px);
    }
  `}

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: clamp(0.625rem, 1vw, 0.75rem) clamp(1rem, 1.5vw, 1.25rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.875vw, 0.6875rem) clamp(0.875rem, 1.25vw, 1rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: clamp(1.5rem, 2.5vw, 2rem);
  
  .spinner {
    width: clamp(1.5rem, 2.5vw, 2rem);
    height: clamp(1.5rem, 2.5vw, 2rem);
    border: clamp(0.125rem, 0.1875vw, 0.1875rem) solid #f3f4f6;
    border-top: clamp(0.125rem, 0.1875vw, 0.1875rem) solid #4299e1;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    padding: clamp(1.25rem, 2vw, 1.5rem);
    
    .spinner {
      width: clamp(1.25rem, 2vw, 1.5rem);
      height: clamp(1.25rem, 2vw, 1.5rem);
      border: clamp(0.0625rem, 0.125vw, 0.125rem) solid #f3f4f6;
      border-top: clamp(0.0625rem, 0.125vw, 0.125rem) solid #4299e1;
    }
  }

  @media (max-width: 480px) {
    padding: clamp(1rem, 1.5vw, 1.25rem);
    
    .spinner {
      width: clamp(1rem, 1.5vw, 1.25rem);
      height: clamp(1rem, 1.5vw, 1.25rem);
      border: clamp(0.0625rem, 0.125vw, 0.125rem) solid #f3f4f6;
      border-top: clamp(0.0625rem, 0.125vw, 0.125rem) solid #4299e1;
    }
  }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #fc8181, #f56565);
  color: white;
  padding: clamp(0.75rem, 1.125vw, 0.875rem) clamp(0.875rem, 1.25vw, 1rem);
  border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
  text-align: center;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(245, 101, 101, 0.3);
  margin-bottom: clamp(0.875rem, 1.25vw, 1rem);

  @media (max-width: 768px) {
    padding: clamp(0.625rem, 1vw, 0.75rem) clamp(0.75rem, 1.125vw, 0.875rem);
    font-size: clamp(0.6875rem, 0.875vw, 0.75rem);
    border-radius: clamp(0.5rem, 0.875vw, 0.625rem);
  }

  @media (max-width: 480px) {
    padding: clamp(0.5625rem, 0.875vw, 0.6875rem) clamp(0.625rem, 1vw, 0.75rem);
    font-size: clamp(0.625rem, 0.75vw, 0.6875rem);
    border-radius: clamp(0.4375rem, 0.75vw, 0.5625rem);
  }
`;

const UserManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    employeeId: '',
    password: '',
    role: 'user',
    status: 'active'
  });

  // Fetch users on component mount
  useEffect(() => {
    // Only fetch users if current user is admin
    if (currentUser && currentUser.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getAllUsers();

      // Transform the data to match our frontend structure
      const transformedUsers = response.map(user => ({
        id: user.id,
        fullName: user.name,
        employeeId: user.employee_id,
        role: user.isAdmin ? 'admin' : 'user',
        status: user.isActive ? 'active' : 'inactive',
        avatar: '👨‍💼',
        createdAt: user.createdAt || new Date().toISOString().split('T')[0]
      }));

      setUsers(transformedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle body scroll locking when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  // Handle keyboard events for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };

    if (showModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showModal]);

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    let matches = true;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      matches = matches && (
        user.fullName.toLowerCase().includes(search) ||
        user.employeeId.toLowerCase().includes(search)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      matches = matches && user.role === roleFilter;
    }

    // Status filter
    if (statusFilter !== 'all') {
      matches = matches && user.status === statusFilter;
    }

    return matches;
  });

  // Calculate statistics
  const stats = {
    total: users.length,
    admins: users.filter(user => user.role === 'admin').length,
    users: users.filter(user => user.role === 'user').length,
    active: users.filter(user => user.status === 'active').length
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);

    if (user && type === 'edit') {
      setFormData({
        name: user.fullName,
        employeeId: user.employeeId,
        password: '',
        role: user.role,
        status: user.status
      });
    } else {
      setFormData({
        name: '',
        employeeId: '',
        password: '',
        role: 'user',
        status: 'active'
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setSelectedUser(null);
    setFormData({
      name: '',
      employeeId: '',
      password: '',
      role: 'user',
      status: 'active'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalType === 'add') {
        await apiService.createUser({
          name: formData.name,
          employee_id: formData.employeeId,
          password: formData.password,
          isAdmin: formData.role === 'admin',
          isActive: formData.status === 'active'
        });

        // Refresh the users list
        await fetchUsers();
      } else if (modalType === 'edit') {
        const updateData = {
          name: formData.name,
          employee_id: formData.employeeId,
          isAdmin: formData.role === 'admin',
          isActive: formData.status === 'active'
        };

        // Only include password if it's provided
        if (formData.password) {
          updateData.password = formData.password;
        }

        await apiService.updateUser(selectedUser.id, updateData);

        // Refresh the users list
        await fetchUsers();
      }

      closeModal();
    } catch (error) {
      console.error('Failed to save user:', error);
      setError(error.message || 'Failed to save user. Please try again.');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await apiService.deleteUser(userId);

        // Refresh the users list
        await fetchUsers();

        // Show success message (optional)
        console.log('User deleted successfully');
      } catch (error) {
        console.error('Failed to delete user:', error);
        setError(error.message || 'Failed to delete user. Please try again.');
      }
    }
  };

  const getRandomAvatar = () => {
    const avatars = ['‍��', '‍��', '‍��', '‍��', '‍🎯', '‍🎯', '‍🏫', '👩‍🏫'];
    return avatars[Math.floor(Math.random() * avatars.length)];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Check if user has admin privileges (this should be before loading check)
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <Container>
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}></div>
          <h2 style={{ marginBottom: '16px', color: '#333' }}>Access Denied</h2>
          <p style={{ fontSize: '16px', lineHeight: '1.6' }}>
            You don't have permission to access user management.
            This feature is only available to administrators.
          </p>
        </div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <div className="spinner"></div>
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Header>
        <Title>
          <FaUsers />
          User Management
        </Title>

        <HeaderActions>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>

          <FilterSelect
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </FilterSelect>

          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </FilterSelect>

          <AddButton onClick={() => openModal('add')}>
            <FaPlus /> Add User
          </AddButton>
        </HeaderActions>
      </Header>

      <StatsRow>
        <StatCard>
          <h3>{stats.total}</h3>
          <p>Total Users</p>
        </StatCard>
        <StatCard>
          <h3>{stats.admins}</h3>
          <p>Administrators</p>
        </StatCard>
        <StatCard>
          <h3>{stats.users}</h3>
          <p>Regular Users</p>
        </StatCard>
        <StatCard>
          <h3>{stats.active}</h3>
          <p>Active</p>
        </StatCard>
      </StatsRow>

      <UsersGrid>
        {filteredUsers.map(user => (
          <UserCard key={user.id} isAdmin={user.role === 'admin'}>
            <UserHeader>
              <UserAvatar isAdmin={user.role === 'admin'}>
                {user.fullName.charAt(0).toUpperCase()}
              </UserAvatar>
              <UserInfo>
                <UserName>{user.fullName}</UserName>
                <UserRole role={user.role}>
                  {user.role === 'admin' ? <FaUserShield /> : <FaUser />}
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </UserRole>
              </UserInfo>
            </UserHeader>

            <CardContent>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', marginBottom: '16px' }}>
                <UserEmployeeId>
                  <FaIdCard />
                  Emp ID: {user.employeeId}
                </UserEmployeeId>

                <StatusBadge status={user.status}>
                  {user.status === 'active' ? '✓' : '✕'} {user.status}
                </StatusBadge>
              </div>

              {/* Account Activity */}
              <InfoSection>
                <InfoList>
                  <InfoItem>
                    <FaClock style={{ color: '#10b981' }} />
                    Created: {formatDate(user.createdAt)}
                  </InfoItem>
                </InfoList>
              </InfoSection>
            </CardContent>

            <UserActions>
              <ActionButton type="edit" onClick={() => openModal('edit', user)}>
                <FaEdit /> Edit User
              </ActionButton>
              {user.id !== currentUser?.id && (
                <ActionButton type="delete" onClick={() => handleDelete(user.id)}>
                  <FaTrash /> Delete
                </ActionButton>
              )}
            </UserActions>
          </UserCard>
        ))}
      </UsersGrid>

      {/* Modal */}
      {showModal && ReactDOM.createPortal(
        <Modal>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {modalType === 'add' ? 'Add New User' : 'Edit User'}
              </ModalTitle>
              <CloseButton onClick={closeModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Employee ID (will be used for login)</Label>
                <Input
                  type="text"
                  placeholder="Enter employee ID"
                  value={formData.employeeId}
                  onChange={(e) => setFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Password {modalType === 'edit' && '(Leave blank to keep current password)'}</Label>
                <PasswordGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required={modalType === 'add'}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </PasswordGroup>
              </FormGroup>

              <FormGroup>
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormGroup>

              <ModalActions>
                <Button type="button" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" primary>
                  <FaSave /> {modalType === 'add' ? 'Create User' : 'Update User'}
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>,
        document.body
      )}
    </Container>
  );
};

export default UserManagement;